import BusinessIcon from '@mui/icons-material/Business';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import BackendService from 'BackendService';
import React, { useCallback, useMemo, useState } from 'react';
import { InvestmentEntity } from '../../models/InvestmentEntity';
import InvestmentEntityContact from '../../models/InvestmentEntityContact';
import { Palette } from 'Palette';
import { StyledButton } from '../styles/StyledButtons';
import { StyledAvatar, StyledCard, StyledHeading, StyledTypography } from '../styles/styles';
import { ExpandableEntityCard } from './ExpandableEntityCard';

interface InvestmentEntitiesSectionProps {
  entities: InvestmentEntity[];
  loading: boolean;
  loaded: boolean;
  filter: string;
  tone?: keyof typeof Palette;
  onFilterChange: (filter: string) => void;
  onLoadEntities: () => void;
  currentContactId?: string;
}

export const InvestmentEntitiesSection: React.FC<InvestmentEntitiesSectionProps> = ({
  entities,
  loading,
  loaded,
  filter,
  tone = 'tone1',
  onFilterChange,
  onLoadEntities,
  currentContactId,
}) => {
  // single expand id
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // contacts cache + loading flags
  const [contactsMap, setContactsMap] = useState<
    Record<InvestmentEntity['id'], InvestmentEntityContact[] | undefined>
  >({});
  const [contactsLoading, setContactsLoading] = useState<Record<string, boolean>>({});

  const getEntityTypeLabel = (type: string) =>
    (
      ({
        INDIVIDUAL: 'Individual',
        TRUST: 'Trust',
        COMPANY: 'Company',
        FUND: 'Fund',
        PARTNERSHIP: 'Partnership',
        OTHER: 'Other',
      }) as Record<string, string>
    )[type] || type;

  const getEntityTypeColor = (type: string) =>
    (
      ({
        INDIVIDUAL: '#4CAF50',
        TRUST: '#2196F3',
        COMPANY: '#FF9800',
        FUND: '#9C27B0',
        PARTNERSHIP: '#F44336',
        OTHER: '#607D8B',
      }) as Record<string, string>
    )[type] || '#607D8B';

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return entities;
    return entities.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        e.type.toLowerCase().includes(q) ||
        (e.description || '').toLowerCase().includes(q),
    );
  }, [entities, filter]);

  const maybeLoadContacts = useCallback(
    async (entityId: string) => {
      if (contactsLoading[entityId]) return; // already loading
      if (contactsMap[entityId] !== undefined) return; // already loaded (even empty)

      setContactsLoading((prev) => ({ ...prev, [entityId]: true }));
      try {
        const list = await new BackendService().getEntityContacts(entityId);
        setContactsMap((prev) => ({ ...prev, [entityId]: list || [] }));
      } catch (e) {
        console.error('Failed to load contacts for entity', entityId, e);
        setContactsMap((prev) => ({ ...prev, [entityId]: [] }));
      } finally {
        setContactsLoading((prev) => ({ ...prev, [entityId]: false }));
      }
    },
    [contactsLoading, contactsMap],
  );

  const toggleExpand = useCallback(
    (entityId: string) => {
      if (expandedId === entityId) {
        setExpandedId(null);
        return;
      }
      setExpandedId(entityId); // open immediately; spinner shows in card
      void maybeLoadContacts(entityId);
    },
    [expandedId, maybeLoadContacts],
  );

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 1,
        }}
      >
        <StyledHeading tone={tone}>Investment Entities</StyledHeading>
      </Box>

      {/* Load */}
      {!loaded && !loading && (
        <StyledButton onClick={onLoadEntities}>Load Investment Entities</StyledButton>
      )}

      {/* Loading */}
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" p={3}>
          <CircularProgress size={24} />
          <Typography sx={{ ml: 2 }} variant="body2" color="text.secondary">
            Loading investment entities...
          </Typography>
        </Box>
      )}

      {/* Empty */}
      {loaded && !loading && entities.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          No investment entities found for this contact.
        </Typography>
      )}

      {/* Content */}
      {loaded && !loading && entities.length > 0 && (
        <>
          {/* Filter + Summary */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
            <TextField
              placeholder="Filter by name, type, or description..."
              value={filter}
              onChange={(e) => onFilterChange(e.target.value)}
              fullWidth
              size="small"
              variant="outlined"
            />
            <Box
              sx={{
                minWidth: 220,
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {filtered.length === entities.length
                  ? `Showing all ${entities.length}`
                  : `Showing ${filtered.length} of ${entities.length}`}
              </Typography>
            </Box>
          </Stack>

          {/* List (single expand) */}
          <Box sx={{ mt: 1 }}>
            {filtered.map((entity) => {
              const open = expandedId === entity.id;
              return (
                <Box key={entity.id} sx={{ mb: 1 }}>
                  <StyledCard tone={tone} sx={{ p: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minHeight: 44 }}>
                      <StyledAvatar
                        tone={tone}
                        sx={{
                          width: 18,
                          height: 18,
                          fontSize: '0.55rem',
                          backgroundColor: getEntityTypeColor(entity.type),
                        }}
                      >
                        <BusinessIcon sx={{ fontSize: '0.7rem' }} />
                      </StyledAvatar>

                      <StyledTypography
                        fontWeight={700}
                        tone={tone}
                        sx={{
                          fontSize: '0.8rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                        }}
                        title={entity.name}
                      >
                        {entity.name}
                      </StyledTypography>

                      <Chip
                        label={getEntityTypeLabel(entity.type)}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: '0.6rem',
                          height: 18,
                          borderColor: getEntityTypeColor(entity.type),
                          color: getEntityTypeColor(entity.type),
                        }}
                      />

                      <IconButton
                        size="small"
                        onClick={() => toggleExpand(entity.id)}
                        sx={{ ml: 0.5 }}
                        aria-label={open ? 'Collapse' : 'Expand'}
                      >
                        {open ? (
                          <ExpandLessIcon fontSize="small" />
                        ) : (
                          <ExpandMoreIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Stack>

                    {open && (
                      <Box sx={{ mt: 1 }}>
                        <ExpandableEntityCard
                          key={entity.id}
                          entity={entity}
                          tone={tone}
                          expanded
                          contacts={contactsMap[entity.id]}
                          contactsLoading={!!contactsLoading[entity.id]} // spinner while loading
                          onExpandedChange={(val) => !val && setExpandedId(null)}
                          currentContactId={currentContactId}
                        />
                      </Box>
                    )}
                  </StyledCard>
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
};
